"use client";

import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Customized } from "recharts";
import { useEffect, useState } from "react";

import type { TooltipProps } from "recharts";
import type { NameType, ValueType } from "recharts/types/component/DefaultTooltipContent";
import type { RecTransitionParam, RankTransitionParam } from "../_lib/games-transition";

const CustomTooltip = ({
  active,
  payload,
  label,
  popNames,
  reversed,
}: TooltipProps<ValueType, NameType> & { popNames: { [key: string]: string }; reversed: boolean }) => {
  if (active && payload && payload.length) {
    const data = payload.map((m) => ({ name: m.name, value: m.value, color: m.color }));
    data.sort((a, b) => (reversed ? Number(a.value) - Number(b.value) : Number(b.value) - Number(a.value)));
    return (
      <div className="bg-slate-800/70 rounded-xl px-2 items-center mx-auto">
        <p className="text-center">{`${label}`}</p>
        {data.map((m) => (
          <div key={"tooltip" + m.name} className="grid grid-cols-2 gap-2 place-items-center">
            <p style={{ textDecorationColor: m.color }} className="underline decoration-4">
              {popNames[String(m.name)]}
            </p>
            <p>{m.value}</p>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

function Overlay(props: any) {
  const [count, setCount] = useState(0);
  const { width, height, durationSeconds } = props;

  useEffect(() => {
    if (width <= 0 || durationSeconds <= 0) return;

    const steps = width;
    const interval = durationSeconds / steps;

    let current = 0;
    const intervalId = setInterval(() => {
      current += 1;
      setCount(current);

      if (current >= width) {
        clearInterval(intervalId);
      }
    }, interval);

    return () => clearInterval(intervalId);
  }, [durationSeconds, width]);

  return (
    <g>
      <rect x={count + 20} y={0} width={width} height={height} fill="rgba(2, 6, 24, 1)" />
    </g>
  );
}

type Props = {
  transition: (RecTransitionParam | RankTransitionParam)[];
  dataSetting: { id: string; data: string; color: string }[];
  popNames: { [key: string]: string };
  type: "rec" | "rank";
};
export function TransitionGraph({ transition, dataSetting, popNames, type }: Props) {
  const [chartKey, setChartKey] = useState(0);
  const [speed, setSpeed] = useState(1);

  const [range, setRange] = useState([0, transition.length]);

  const handlSpeedSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSpeed(Number(e.target.value));
    setChartKey((prev) => prev + 1);
  };
  const handlRange0Select = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRange([Number(e.target.value), range[1]]);
    setChartKey((prev) => prev + 1);
  };
  const handlRange1Select = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRange([range[0], Number(e.target.value)]);
    setChartKey((prev) => prev + 1);
  };
  const domain = type === "rank" ? [0.5, 6.5] : undefined;
  const ticks = type === "rank" ? [1, 2, 3, 4, 5, 6] : undefined;
  return (
    <div className="bg-slate-950  container mx-auto">
      <ResponsiveContainer height="40%" width="100%" aspect={4.0 / 3.0}>
        <LineChart data={transition.slice(range[0], range[1])} margin={{ top: 5, right: 15, left: 10, bottom: 5 }}>
          {dataSetting.map((m) => {
            return (
              <Line
                key={chartKey + m.id}
                type="monotone"
                dot={false}
                dataKey={m.data}
                stroke={m.color}
                isAnimationActive={type === "rec"}
                animationDuration={3000 / speed}
                animationEasing={"linear"}
                activeDot={{ stroke: m.color, strokeWidth: 2, r: 3 }}
                strokeWidth={2}
              />
            );
          })}
          {type === "rank" && <Customized component={<Overlay key={chartKey} durationSeconds={3000 / speed} />} />}
          <Tooltip content={<CustomTooltip popNames={popNames} reversed={type === "rank"} />} />
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis domain={domain} reversed={type === "rank"} ticks={ticks} />
        </LineChart>
      </ResponsiveContainer>
      <div className="flex items-center pb-2 @container">
        <button className="ml-8 link" onClick={() => setChartKey((prev) => prev + 1)}>
          <svg width="20" height="20" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M3.24182 2.32181C3.3919 2.23132 3.5784 2.22601 3.73338 2.30781L12.7334 7.05781C12.8974 7.14436 13 7.31457 13 7.5C13 7.68543 12.8974 7.85564 12.7334 7.94219L3.73338 12.6922C3.5784 12.774 3.3919 12.7687 3.24182 12.6782C3.09175 12.5877 3 12.4252 3 12.25V2.75C3 2.57476 3.09175 2.4123 3.24182 2.32181ZM4 3.57925V11.4207L11.4288 7.5L4 3.57925Z"
              fill="currentColor"
            ></path>
          </svg>
        </button>
        <select className="text-sm w-14 h-5 bg-black text-center" value={speed} onChange={handlSpeedSelect}>
          <option value={0.25}>0.25x</option>
          <option value={0.5}>0.5x</option>
          <option value={1}>1x</option>
          <option value={2}>2x</option>
        </select>
        <span className="ml-8 sm:ml-20 @max-sm:hidden">範囲選択：</span>
        <span id="graph-start" className="ml-2 sm:ml-8">
          始
        </span>
        <select
          aria-labelledby="graph-start"
          className="text-sm w-14 h-5 bg-black text-center"
          value={range[0]}
          onChange={handlRange0Select}
        >
          <option value={0}>0/0</option>
          <option value={transition.length / 3}>1/3</option>
          <option value={(transition.length / 3) * 2}>2/3</option>
        </select>
        <span id="graph-end" className="ml-6">
          終
        </span>
        <select
          aria-labelledby="graph-end"
          className="text-sm w-14 h-5 bg-black text-center"
          value={range[1]}
          onChange={handlRange1Select}
        >
          <option value={transition.length / 3}>1/3</option>
          <option value={(transition.length / 3) * 2}>2/3</option>
          <option value={transition.length}>1/1</option>
        </select>
      </div>
    </div>
  );
}
